import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { CheckCircle, Users, Calendar, CreditCard, Sparkle, TrendUp, Clock, Crown, ArrowDown, FileText, Lock, MinusCircle } from '@phosphor-icons/react';
import StorageUsageBar from '../components/StorageUsageBar';
import AppFooter from '../components/AppFooter';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// ─── Plan list-price constants ───────────────────────────────────────
// PUBLIC list prices for the Compare Plans cards AND the default Subscribe
// checkout math. These stay static so an SA-set per-org override on
// `price_per_user` (e.g. a promo or Free plan) never distorts the public
// pricing surface OR silently drops a paid checkout to ₹0.
// TODO: once we wire explicit discount / promo codes, we can let the
// checkout amount fall back to a negotiated rate — but ONLY when it's
// greater than zero AND flagged as an active discount (not just a Free
// plan leaving the field at its 0 default).
const PLAN_LIST_PRICES = {
  business_monthly: 399,
  business_yearly: 359,  // ~10% off list when billed yearly
  premium_monthly: 699,
  premium_yearly: 629,   // ~10% off premium list when billed yearly
};

export default function BillingPage() {
  const { user, currentOrg, getOrgHeaders, API } = useAuth();
  const { status, refresh, loading } = useSubscription();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [addSeats, setAddSeats] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [showAddSeats, setShowAddSeats] = useState(false);
  const [showReduceSeats, setShowReduceSeats] = useState(false);
  const [reduceTargetSeats, setReduceTargetSeats] = useState(1);
  const [showDowngrade, setShowDowngrade] = useState(false);
  const [fallbackOrder, setFallbackOrder] = useState(null);
  // ─── Currency (INR default / USD) ──────────────────────────────────
  const [currency, setCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem('billing_currency');
      if (saved === 'INR' || saved === 'USD') return saved;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const lang = (navigator.language || '').toLowerCase();
      if (tz === 'Asia/Kolkata' || lang.endsWith('-in')) return 'INR';
    } catch (e) {}
    return 'USD';
  });
  const [pricing, setPricing] = useState(null);
  const [showServiceDesk, setShowServiceDesk] = useState(false);
  const [sdRedirecting, setSdRedirecting] = useState(false);
  const [sdAgents, setSdAgents] = useState(1);
  const [billingProduct, setBillingProduct] = useState('projects'); // 'projects' | 'service_desk'
  // True when we auto-defaulted to USD for an international visitor (no saved
  // choice + non-India locale) — drives the friendly "prices in USD" note.
  const [currencyAutoUsd] = useState(() => {
    try {
      if (localStorage.getItem('billing_currency')) return false;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const lang = (navigator.language || '').toLowerCase();
      return !(tz === 'Asia/Kolkata' || lang.endsWith('-in'));
    } catch (e) { return false; }
  });
  const changeCurrency = (c) => { setCurrency(c); try { localStorage.setItem('billing_currency', c); } catch (e) {} };
  // Live prorated quote from the backend — refreshed whenever the user
  // bumps seat count or flips the billing cycle. Null until the first
  // request resolves; we keep the legacy list-price math as fallback so
  // the UI is never blank.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  // Tracks whether we've already auto-reconciled stuck "initiated" transactions
  // for this page load. Without this guard the effect would loop because
  // setHistory after sync re-triggers it.
  const autoSyncedRef = useRef(false);

  const h = () => ({ withCredentials: true, headers: getOrgHeaders() });
  const canManage = ['owner', 'admin'].includes(currentOrg?.role);

  useEffect(() => {
    if (!currentOrg) return;
    axios.get(`${API}/payments/history`, h()).then(r => setHistory(r.data || [])).catch(() => {});
  }, [currentOrg]);

  // Silent auto-reconcile: when the page loads (or after a Razorpay redirect),
  // any transaction still showing "initiated" gets re-checked against Razorpay.
  // If Razorpay says it's actually paid, we flip status + activate the
  // subscription. This makes the entire post-payment experience seamless —
  // no manual button clicks, no popups for the happy path. The webhook
  // handler does the same job server-to-server, but a fresh customer might
  // not have the webhook configured yet, so this client-side reconcile is
  // the belt-and-suspenders fallback.
  useEffect(() => {
    if (autoSyncedRef.current) return;
    if (!history || !history.length || !canManage) return;
    // Only reconcile RECENT in-progress payments (started in the last 2h).
    // Without this age bound, a stale 'initiated' transaction that happens to
    // be captured on Razorpay's side would silently re-activate the plan the
    // moment the user opens /billing — even after an admin/SA intentionally
    // set the org to expired/free. We only auto-complete the payment the user
    // just started, never resurrect old ones.
    const RECONCILE_WINDOW_MS = 2 * 60 * 60 * 1000;
    const stuck = history.filter(t =>
      t.status === 'initiated'
      && t.created_at
      && (Date.now() - new Date(t.created_at).getTime()) < RECONCILE_WINDOW_MS
    );
    if (!stuck.length) { autoSyncedRef.current = true; return; }
    autoSyncedRef.current = true;
    (async () => {
      let anyPaid = false;
      for (const t of stuck) {
        try {
          const { data } = await axios.post(`${API}/payments/razorpay/sync/${t.transaction_id}`, {}, h());
          if (data?.status === 'paid') anyPaid = true;
        } catch { /* silent — will retry on next page visit */ }
      }
      if (anyPaid) {
        await refresh();
        try {
          const { data: nh } = await axios.get(`${API}/payments/history`, h());
          setHistory(nh || []);
        } catch { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.length, canManage]);

  // Handle ?payment=success|failed|cancelled redirect from Razorpay callback
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (!payment) return;
    if (payment === 'success') {
      // Refresh subscription + history. The auto-sync effect above will
      // pick up any still-pending transactions and reconcile them with
      // Razorpay so the user sees ACTIVE + the right seat count without
      // any manual action.
      refresh();
      axios.get(`${API}/payments/history`, h()).then(r => setHistory(r.data || [])).catch(() => {});
    } else if (payment === 'failed') {
      alert('Payment failed or could not be verified. Please try again or contact support.');
    } else if (payment === 'cancelled') {
      alert('Payment was cancelled.');
    }
    searchParams.delete('payment');
    setSearchParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize seat count when opening dialog. For a RENEWAL (expired paid
  // sub) we pre-fill the number of seats the workspace already uses so the
  // admin renews for their current team in one click. For a fresh subscribe
  // / add-seats we default to 1 (solo founders can bump up).
  useEffect(() => {
    if (showAddSeats && status) {
      const expired = status.plan === 'expired'
        || (status.plan === 'active' && status.current_period_end && new Date(status.current_period_end) < new Date());
      setAddSeats(expired ? Math.max(1, status.used_seats || 1) : 1);
      setQuote(null);
    }
  }, [showAddSeats, status]);

  // Deep-link: /billing?renew=1 (from the app-wide Expired banner) auto-opens
  // the renewal dialog so the admin lands straight on checkout.
  useEffect(() => {
    if (searchParams.get('renew') === '1' && status && canManage) {
      setBillingCycle('monthly');
      setShowAddSeats(true);
      searchParams.delete('renew');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Reset the reduce-seats target whenever the modal opens so we don't carry
  // stale state from a previous open.
  useEffect(() => {
    if (showReduceSeats && status) {
      setReduceTargetSeats(Math.max(status.user_count || 1, status.billable_users || 1));
    }
  }, [showReduceSeats, status]);

  // Fetch a fresh quote whenever the user changes seat count or cycle while
  // the Add Seats modal is open. Debounced via React's normal batching;
  // we don't bother with a setTimeout because the +/- buttons fire one
  // event at a time. `target_total` is the projected new total seat count.
  useEffect(() => {
    if (!showAddSeats || !status || !canManage || currency === 'USD') { if (currency === 'USD') setQuote(null); return; }
    // Period-aware: an expired paid sub still has plan==='active' but a past
    // period_end — treat it as NOT active so the quote charges a full cycle
    // for the seats being renewed (not a proration of "added" seats).
    const isActiveLocal = status.plan === 'active' && status.current_period_end && new Date(status.current_period_end) > new Date();
    const target = isActiveLocal ? (status.billable_users + addSeats) : addSeats;
    setQuoteLoading(true);
    axios.get(`${API}/payments/seat-quote`, { ...h(), params: { seat_count: target, billing_cycle: billingCycle } })
      .then(r => setQuote(r.data))
      .catch(() => setQuote(null))
      .finally(() => setQuoteLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddSeats, addSeats, billingCycle, status?.billable_users, currency]);

  // Fetch the currency-aware pricing surface whenever the currency changes.
  useEffect(() => {
    axios.get(`${API}/payments/pricing`, { ...h(), params: { currency } })
      .then(r => setPricing(r.data))
      .catch(() => setPricing(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const handleStartTrial = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/payments/upgrade-to-business`, {}, h());
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start trial');
    } finally { setProcessing(false); }
  };

  const handleResume = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/payments/resume-subscription`, {}, h());
      await refresh();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to resume subscription'); }
    finally { setProcessing(false); }
  };

  const handleDowngrade = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/payments/downgrade-to-free`, {}, h());
      await refresh();
      setShowDowngrade(false);
      if (data?.cancel_at_period_end && data?.access_until) {
        alert(data.message || `Subscription cancelled. You retain Business access until ${new Date(data.access_until).toLocaleDateString()}.`);
      }
    } catch (err) { alert(err.response?.data?.detail || 'Failed to downgrade'); }
    finally { setProcessing(false); }
  };

  // Reduce paid seat count. No refund / credit for the current cycle by
  // design — the customer keeps the seats they already paid for and the
  // lower seat count kicks in only at the next renewal. Backend enforces
  // new_seat_count >= active member count.
  const handleReduceSeats = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/payments/update-seats`, { new_seat_count: reduceTargetSeats }, h());
      await refresh();
      setShowReduceSeats(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reduce seats');
    } finally { setProcessing(false); }
  };

  // Open the renewal popup (expired subscription). Defaults to monthly; the
  // seat-count effect pre-fills the seats already in use.
  const openRenew = () => {
    setBillingCycle('monthly');
    setShowAddSeats(true);
  };

  const startCheckout = async (seatCount, cycle) => {
    setProcessing(true);
    try {
      const { data: order } = await axios.post(`${API}/payments/razorpay/order`,
        { seat_count: seatCount, billing_cycle: cycle, currency }, h());
      if (!order.key_id) { alert('Razorpay is not configured on the server. Please contact support.'); return; }

      // Close our dialog first (Shadcn focus-trap can interfere)
      setShowAddSeats(false);
      await new Promise(r => setTimeout(r, 150));

      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) { alert('Failed to load Razorpay. Please check your internet connection.'); return; }

      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: 'Inspiro Projects', description: `${seatCount} seat${seatCount > 1 ? 's' : ''} · ${cycle}`,
        order_id: order.order_id,
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#4f46e5' },
        modal: { ondismiss: () => { setProcessing(false); }, escape: true, confirm_close: true },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, h());
            await refresh();
            const { data: nh } = await axios.get(`${API}/payments/history`, h());
            setHistory(nh || []);
            alert('Payment successful! Subscription activated.');
          } catch (err) { alert(err.response?.data?.detail || 'Payment verification failed'); }
        },
      });
      rzp.on('payment.failed', (resp) => {
        console.error('[razorpay] payment.failed', resp);
        alert('Payment failed: ' + (resp.error?.description || 'Unknown error'));
      });
      rzp.open();

      // Fallback: if Razorpay modal doesn't render visibly within 2.5s (blocked by environment),
      // show a fallback banner with the order details and a payment-page link.
      setTimeout(() => {
        const container = document.querySelector('.razorpay-container');
        const visible = container && container.offsetWidth > 0 && container.offsetHeight > 0;
        if (!visible) {
          console.warn('[razorpay] modal not visible — showing fallback');
          setFallbackOrder({
            order_id: order.order_id, amount: order.amount, key_id: order.key_id,
            seat_count: seatCount, cycle,
          });
        }
      }, 2500);
    } catch (err) {
      console.error('[razorpay] checkout error', err);
      alert(err.response?.data?.detail || err.message || 'Failed to initiate payment');
    } finally { setProcessing(false); }
  };

  // Service Desk add-on checkout — buys N agents in the selected currency.
  const startServiceDeskCheckout = async (agents, cycle) => {
    setProcessing(true);
    try {
      const { data: order } = await axios.post(`${API}/payments/razorpay/service-desk-order`,
        { agent_count: agents, billing_cycle: cycle, currency }, h());
      if (!order.key_id) { alert('Razorpay is not configured on the server. Please contact support.'); return; }
      setShowServiceDesk(false);
      await new Promise(r => setTimeout(r, 150));
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) { alert('Failed to load Razorpay. Please check your internet connection.'); return; }
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: 'Inspiro Service Desk', description: `${agents} agent${agents > 1 ? 's' : ''} · ${cycle}`,
        order_id: order.order_id,
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#f97316' },
        modal: { ondismiss: () => { setProcessing(false); }, escape: true, confirm_close: true },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, h());
            await refresh();
            const { data: nh } = await axios.get(`${API}/payments/history`, h());
            setHistory(nh || []);
            alert('Payment successful! Service Desk agents added.');
          } catch (err) { alert(err.response?.data?.detail || 'Payment verification failed'); }
        },
      });
      rzp.on('payment.failed', (resp) => alert('Payment failed: ' + (resp.error?.description || 'Unknown error')));
      rzp.open();

      // Fallback: if the native modal is blocked (sandboxed/iframed preview or
      // a popup blocker) and doesn't render within 2.5s, redirect the browser
      // to Razorpay's hosted checkout page for the same agents/cycle. On return
      // the /billing?payment=success redirect + auto-sync reconcile the txn and
      // grant the agents.
      setTimeout(async () => {
        const container = document.querySelector('.razorpay-container');
        const visible = container && container.offsetWidth > 0 && container.offsetHeight > 0;
        if (visible) return;
        console.warn('[razorpay] SD modal not visible — falling back to hosted checkout');
        setSdRedirecting(true);
        try {
          const { data } = await axios.post(`${API}/payments/razorpay/service-desk-payment-link`,
            { agent_count: agents, billing_cycle: cycle, currency }, h());
          if (data.payment_url) { window.location.href = data.payment_url; return; }
          setSdRedirecting(false);
          alert('Could not open the payment page. Please try again.');
        } catch (e) {
          setSdRedirecting(false);
          alert(e?.response?.data?.detail || 'Could not open the payment page. Please try again.');
        }
      }, 1800);
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Failed to initiate payment');
    } finally { setProcessing(false); }
  };

  // Directly open Razorpay's HOSTED checkout page (payment link) for the
  // given seats/cycle. This is the reliable path for UPI / Net Banking —
  // especially inside sandboxed/iframed previews where the native modal is
  // blocked. Redirects the browser to Razorpay's page; on return the
  // /billing?payment=success redirect + auto-sync reconcile the txn.
  const startHostedCheckout = async (seatCount, cycle) => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/payments/razorpay/payment-link`,
        { seat_count: seatCount, billing_cycle: cycle }, h());
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        alert('Failed to open payment page. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to open payment page.');
      setProcessing(false);
    }
  };

  // Opens Razorpay hosted checkout via Payment Link (most reliable fallback for preview environments)
  const openHostedCheckout = async () => {
    if (!fallbackOrder) return;
    try {
      setProcessing(true);
      const { data } = await axios.post(`${API}/payments/razorpay/payment-link`,
        { seat_count: fallbackOrder.seat_count, billing_cycle: fallbackOrder.cycle }, h());
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        alert('Failed to open payment page. Please try again.');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to open payment page.');
    } finally { setProcessing(false); }
  };

  if (!status) return <div className="p-8 text-zinc-400 text-sm">Loading billing…</div>;
  const isFree = status.plan === 'free';
  const isTrial = status.plan === 'trial';
  const isActive = status.plan === 'active';
  // Period-aware "active now" — false once the paid window has lapsed even
  // though plan may still read 'active'. Drives renewal vs add-seats logic.
  const isActiveNow = status.plan === 'active' && status.current_period_end && new Date(status.current_period_end) > new Date();
  const isPremiumTier = status.plan_tier === 'premium';
  const isInternal = status.is_internal === true;
  // Lazy-check expired paid subscription. Catches BOTH the SA-settable
  // `plan='expired'` lifecycle AND an `active` sub whose period has lapsed.
  // Internal orgs ("Lifetime Free") are exempt — no expiry rendering at all.
  const isExpired = !isInternal && (status.plan === 'expired'
    || (isActive && status.current_period_end && new Date(status.current_period_end) < new Date()));
  // Card title: swap to "Premium Plan" when plan_tier is premium, or
  // "Plan Expired" when period has lapsed so the user isn't misled into
  // thinking everything still works.
  const planTitle = isInternal
    ? `${isPremiumTier ? 'Premium' : isFree ? 'Free' : 'Business'} Plan · Internal`
    : isExpired
    ? (isPremiumTier ? 'Premium Plan (Expired)' : 'Business Plan (Expired)')
    : isFree ? 'Free Plan'
    : isTrial ? `${isPremiumTier ? 'Premium' : 'Business'} Trial`
    : isPremiumTier ? 'Premium Plan'
    : 'Business Plan';
  const badgeLabel = isInternal ? 'LIFETIME FREE'
    : isExpired ? 'EXPIRED'
    : status.plan === 'trial' ? 'TRIAL'
    : isPremiumTier ? 'PREMIUM'
    : status.plan.toUpperCase();
  const badgeClass = isInternal
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : isExpired
    ? 'bg-red-100 text-red-700 border border-red-200'
    : isActive ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : isTrial ? 'bg-amber-100 text-amber-700 border border-amber-200'
    : 'bg-zinc-100 text-zinc-700 border border-zinc-200';
  // Checkout amount follows the target plan_tier's list price. SA-set
  // `price_per_user` is no longer the default because a Free org has it
  // zeroed, which silently dropped the checkout to ₹0 before this change.
  const sym = pricing?.symbol || (currency === 'USD' ? '$' : '₹');
  const usdMode = currency === 'USD';
  const planP = pricing ? (isPremiumTier ? pricing.premium : pricing.business) : null;
  const price = planP
    ? (billingCycle === 'yearly' ? planP.yearly_per_month : planP.monthly)
    : (isPremiumTier
        ? PLAN_LIST_PRICES.premium_monthly
        : (billingCycle === 'yearly' ? PLAN_LIST_PRICES.business_yearly : PLAN_LIST_PRICES.business_monthly));
  // Actual amount charged (per-year for yearly, per-month for monthly).
  const chargeUnit = planP ? (billingCycle === 'yearly' ? planP.yearly_per_year : planP.monthly) : price;
  // Min seats: 1 (allows solo founders to subscribe with just themselves).
  // If they currently have N active members, the backend will still enforce
  // their seat cap on future invites, but we don't force them to pay for
  // existing members upfront.
  const minSeats = 1;
  const totalAmount = chargeUnit * addSeats;
  // USD checkout doesn't use the INR-only proration quote — show the fixed
  // USD amount directly. INR keeps the live quote.
  const payNowAmount = usdMode ? totalAmount : (quote ? Math.round(quote.amount / 100) : totalAmount);

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50" data-testid="billing-page">
      {sdRedirecting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/70 backdrop-blur-sm" data-testid="sd-redirecting-overlay">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-8 py-6 shadow-xl ring-1 ring-zinc-200">
            <span className="h-8 w-8 rounded-full border-[3px] border-orange-500 border-t-transparent animate-spin" />
            <p className="text-sm font-semibold text-zinc-800">Opening secure checkout…</p>
            <p className="text-xs text-zinc-400">Redirecting you to Razorpay</p>
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="mb-8">
          <h1 className="font-['Outfit'] text-3xl font-bold text-zinc-900 tracking-tight">Billing & Plans</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage your subscription, seats and payments</p>
        </div>

        {/* CURRENT STATUS */}
        {isInternal && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="internal-banner">
            <Sparkle weight="fill" size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-emerald-900">
                Internal Workspace — Lifetime Free
              </p>
              <p className="text-[11px] text-emerald-700 mt-0.5">
                This workspace is marked as an internal Inspiro organization. Billing, renewals, and payment are fully waived for life. Feature access follows your plan tier ({isPremiumTier ? 'Premium' : isFree ? 'Free' : 'Business'}).
              </p>
            </div>
          </div>
        )}
        {isExpired && !isInternal && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="expired-banner">
            <Clock weight="fill" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-red-900">
                Your {isPremiumTier ? 'Premium' : 'Business'} subscription expired on {new Date(status.current_period_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
              <p className="text-[11px] text-red-700 mt-0.5">Features reverted to Free tier. Renew to restore Docs, Whiteboard, Slack / Teams, CSV import, and advanced insights.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={openRenew} className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0" data-testid="expired-renew-now">
                Renew now
              </Button>
            )}
          </div>
        )}
        {status.cancel_at_period_end && status.current_period_end && !isExpired && !isInternal && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="cancellation-banner">
            <Clock weight="fill" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-amber-900">Subscription cancelled — access continues until {new Date(status.current_period_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              <p className="text-[11px] text-amber-700 mt-0.5">You still have full Business Plan access. After this date your workspace will switch to the Free plan (max {status.free_plan_limit} members). You can resume anytime.</p>
            </div>
          </div>
        )}

        <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6" data-testid="subscription-status-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="font-['Outfit'] text-xl font-bold" data-testid="plan-title">
                  {planTitle}
                </h2>
                <Badge className={`text-[11px] ${badgeClass}`}>{badgeLabel}</Badge>
              </div>
              <div className="flex items-center gap-5 text-sm text-zinc-600">
                <div className="flex items-center gap-1.5"><Users size={14} /> {status.used_seats} / {isFree ? status.free_plan_limit : (isTrial ? '∞' : status.billable_users)} seats used</div>
                {!isInternal && (isTrial || isActive) && status.current_period_end && (
                  <div className="flex items-center gap-1.5" data-testid="plan-valid-until">
                    <Calendar size={14} />
                    <span>
                      Valid until <strong className="font-semibold text-zinc-800">{new Date(status.current_period_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                      {status.days_remaining !== null && (
                        <span className={`ml-1.5 ${status.days_remaining <= 7 ? 'text-amber-600 font-medium' : 'text-zinc-400'}`}>
                          ({status.days_remaining} day{status.days_remaining !== 1 ? 's' : ''} left)
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {isTrial && !status.current_period_end && !isInternal && status.days_remaining !== null && (
                  <div className="flex items-center gap-1.5"><Calendar size={14} /> {status.days_remaining} day{status.days_remaining !== 1 ? 's' : ''} remaining</div>
                )}
                {isActive && <div className="flex items-center gap-1.5"><CreditCard size={14} /> {status.billing_cycle || 'monthly'}</div>}
              </div>
            </div>
            {canManage && !isInternal && (isExpired ? (
              <Button onClick={openRenew} disabled={processing} className="bg-red-600 hover:bg-red-700 text-white" data-testid="renew-subscription-button">
                <CreditCard size={14} className="mr-1.5" /> Renew Subscription
              </Button>
            ) : isFree ? (
              status.trial_used ? (
                <Button onClick={() => setShowAddSeats(true)} disabled={processing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="subscribe-button">
                  <Crown size={14} className="mr-1.5" /> Subscribe to Business
                </Button>
              ) : (
                <Button onClick={handleStartTrial} disabled={processing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="start-trial-button">
                  <Sparkle size={14} className="mr-1.5" /> Start 14-day Business Trial
                </Button>
              )
            ) : isTrial ? (
              <Button onClick={() => setShowAddSeats(true)} disabled={processing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="subscribe-button">
                <Crown size={14} className="mr-1.5" /> Subscribe Now
              </Button>
            ) : (
              <div className="flex gap-2">
                {status.cancel_at_period_end ? (
                  <Button onClick={handleResume} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="resume-button">
                    Resume Subscription
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => setShowAddSeats(true)} variant="outline" data-testid="add-seats-button">
                      <TrendUp size={14} className="mr-1.5" /> Add Seats
                    </Button>
                    <Button onClick={() => setShowReduceSeats(true)} variant="outline" data-testid="reduce-seats-button" className="text-zinc-700">
                      <ArrowDown size={14} className="mr-1.5" /> Reduce Seats
                    </Button>
                    <Button onClick={() => setShowDowngrade(true)} variant="ghost" className="text-zinc-500" data-testid="downgrade-button">
                      <ArrowDown size={14} className="mr-1.5" /> Downgrade
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Storage usage — visible to everyone so the workspace knows where they stand. */}
        {status.storage && (
          <div className="mb-6">
            <StorageUsageBar storage={status.storage} />
          </div>
        )}

        {/* PRODUCT SWITCHER — Projects vs Service Desk, each with its own
            plans + full feature showcase. Currency toggle is shared. */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6" data-testid="billing-product-switcher">
          <div className="inline-flex rounded-xl border border-zinc-200 bg-white p-1 shadow-sm">
            <button type="button" onClick={() => setBillingProduct('projects')} data-testid="product-tab-projects"
              className={`px-4 h-9 rounded-lg text-[13px] font-bold tracking-tight transition-colors inline-flex items-center gap-2 ${billingProduct === 'projects' ? 'bg-indigo-600 text-white shadow' : 'text-zinc-600 hover:bg-zinc-50'}`}>
              <Crown size={14} weight="fill" /> Inspiro Projects
            </button>
            <button type="button" onClick={() => setBillingProduct('service_desk')} data-testid="product-tab-service-desk"
              className={`px-4 h-9 rounded-lg text-[13px] font-bold tracking-tight transition-colors inline-flex items-center gap-2 ${billingProduct === 'service_desk' ? 'bg-orange-500 text-white shadow' : 'text-zinc-600 hover:bg-zinc-50'}`}>
              <Sparkle size={14} weight="fill" /> Inspiro Service Desk
            </button>
          </div>
          <div className="flex items-center gap-2" data-testid="currency-toggle">
            <span className="text-[11px] text-zinc-400 mr-1 hidden sm:inline">Yearly saves 10%</span>
            <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5">
              <button type="button" onClick={() => changeCurrency('INR')}
                className={`px-3 h-7 rounded-md text-[12px] font-semibold transition-colors ${currency === 'INR' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
                data-testid="currency-toggle-inr">₹ INR</button>
              <button type="button" onClick={() => changeCurrency('USD')}
                className={`px-3 h-7 rounded-md text-[12px] font-semibold transition-colors ${currency === 'USD' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
                data-testid="currency-toggle-usd">$ USD</button>
            </div>
          </div>
        </div>

        {currency === 'USD' && currencyAutoUsd && (
          <div className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2" data-testid="regional-pricing-note">
            <span className="font-semibold">Prices shown in USD for international customers.</span>
            <button type="button" onClick={() => changeCurrency('INR')} className="underline font-semibold" data-testid="regional-switch-inr">Switch to ₹ INR</button>
          </div>
        )}

        {/* PLANS COMPARISON — always visible so users always understand what each plan unlocks. */}
        {billingProduct === 'projects' ? (
          <PlansComparison
            status={status}
            isFree={isFree}
            isTrial={isTrial}
            isActive={isActive}
            canManage={canManage && !isInternal}
            processing={processing}
            handleStartTrial={handleStartTrial}
            openSubscribe={() => setShowAddSeats(true)}
            openDowngrade={() => setShowDowngrade(true)}
            pricing={pricing}
            currency={currency}
          />
        ) : (
          <ServiceDeskPlans
            status={status}
            canManage={canManage && !isInternal}
            processing={processing}
            pricing={pricing}
            currency={currency}
            openServiceDesk={() => { setSdAgents(1); setShowServiceDesk(true); }}
          />
        )}

        {/* PAYMENT HISTORY */}
        {history.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="payment-history">
            <h3 className="font-['Outfit'] text-lg font-bold mb-4">Payment History</h3>
            <div className="space-y-2">
              {history.map(t => (
                <div key={t.transaction_id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg text-sm" data-testid={`payment-row-${t.transaction_id}`}>
                  <div>
                    <div className="font-semibold text-zinc-900">₹{t.amount_rupees} · {t.desired_seats} seat{t.desired_seats > 1 ? 's' : ''}</div>
                    <div className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] ${t.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                      {t.status === 'initiated' ? 'processing' : t.status}
                    </Badge>
                    {t.status === 'paid' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/invoice/${t.transaction_id}`)}
                        className="h-7 px-2.5 text-[11px]"
                        data-testid={`invoice-button-${t.transaction_id}`}
                      >
                        <FileText size={12} className="mr-1" /> Invoice
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <AppFooter />

      {/* Add Seats / Subscribe dialog */}
      <Dialog open={showAddSeats} onOpenChange={setShowAddSeats}>
        <DialogContent data-testid="subscribe-dialog">
          <DialogHeader>
            <DialogTitle>{isExpired ? 'Renew your subscription' : isActiveNow ? 'Add more seats' : 'Subscribe to Business'}</DialogTitle>
            <DialogDescription>{isExpired ? 'Reactivate your workspace for the seats your team already uses.' : isActiveNow ? 'Purchase additional seats for your workspace.' : 'Pay for seats to continue beyond your trial.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2" data-testid="dialog-currency-toggle">
              <button type="button" onClick={() => changeCurrency('INR')}
                className={`flex-1 h-8 rounded-md text-[12px] font-semibold border transition-colors ${currency === 'INR' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                data-testid="dialog-currency-inr">₹ INR</button>
              <button type="button" onClick={() => changeCurrency('USD')}
                className={`flex-1 h-8 rounded-md text-[12px] font-semibold border transition-colors ${currency === 'USD' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                data-testid="dialog-currency-usd">$ USD</button>
            </div>
            <Tabs value={billingCycle} onValueChange={setBillingCycle}>
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="yearly">Yearly (Save 10%)</TabsTrigger>
              </TabsList>
            </Tabs>
            <div>
              <label className="block text-xs font-semibold text-zinc-600 mb-2">
                {isExpired ? 'Seats to renew' : isActiveNow ? 'Additional seats to add' : 'Number of seats'}
              </label>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => setAddSeats(Math.max(minSeats, addSeats - 1))} data-testid="seat-decrement">−</Button>
                <div className="flex-1 text-center font-bold text-2xl" data-testid="seat-count-display">{addSeats}</div>
                <Button variant="outline" size="icon" onClick={() => setAddSeats(addSeats + 1)} data-testid="seat-increment">+</Button>
              </div>
              {!isActive && status.user_count > addSeats && (
                <p className="text-[11px] text-amber-600 mt-1.5">
                  Heads up — you have {status.user_count} active member{status.user_count !== 1 ? 's' : ''}.
                  Buying {addSeats} seat{addSeats !== 1 ? 's' : ''} now will keep existing members but block new invites until you add more.
                </p>
              )}
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 space-y-2" data-testid="quote-breakdown">
              {quote?.is_proration ? (
                <>
                  <div className="flex items-center justify-between text-[12px] text-zinc-600">
                    <span>{addSeats} new seat{addSeats !== 1 ? 's' : ''} · {quote.days_remaining}/{quote.days_total} days remaining</span>
                    <span className="text-zinc-500">prorated</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-zinc-700">Pay today</span>
                    <span className="font-bold text-indigo-700 text-xl" data-testid="quote-pay-now">₹{Math.round(quote.amount / 100)}</span>
                  </div>
                  <div className="border-t border-indigo-100 pt-2 mt-2">
                    <div className="flex items-center justify-between text-[11px] text-zinc-500">
                      <span>Next invoice on {quote.next_renewal_date ? new Date(quote.next_renewal_date).toLocaleDateString() : '—'}</span>
                      <span>₹{Math.round(quote.next_renewal_amount / 100)}</span>
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">{quote.seat_count} seats × ₹{Math.round(quote.per_seat_full_cycle / 100)} · {quote.billing_cycle}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600">{sym}{price} × {addSeats} seat{addSeats !== 1 ? 's' : ''}{billingCycle === 'yearly' ? ' / mo' : ''}</span>
                    <span className="font-bold text-indigo-700 text-lg" data-testid="quote-pay-now">{sym}{payNowAmount}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">{billingCycle === 'yearly' ? 'Billed yearly (12 months)' : 'Billed monthly (30 days)'}{usdMode ? ' · no GST (international)' : ''}</div>
                </>
              )}
              {quoteLoading && <div className="text-[10px] text-zinc-400">Updating quote…</div>}
            </div>
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={() => startCheckout(isActiveNow ? (status.billable_users + addSeats) : addSeats, billingCycle)}
              disabled={processing}
              data-testid="checkout-button"
            >
              <CreditCard size={14} className="mr-2" /> Pay {sym}{payNowAmount} with Razorpay
            </Button>
            {!usdMode && (
              <button
                type="button"
                onClick={() => startHostedCheckout(isActiveNow ? (status.billable_users + addSeats) : addSeats, billingCycle)}
                disabled={processing}
                className="w-full text-center text-[12px] text-indigo-600 hover:text-indigo-700 hover:underline mt-1 disabled:opacity-50"
                data-testid="hosted-checkout-link"
              >
                Trouble paying? Open the secure checkout page (UPI / Net Banking / Card)
              </button>
            )}
            {usdMode && (
              <p className="text-[11px] text-zinc-500 text-center mt-1" data-testid="usd-contact-sales">
                Need custom USD pricing or an invoice? <a href={`mailto:${pricing?.sales_email || 'sales@inspiroworks.com'}`} className="text-indigo-600 hover:underline">Contact {pricing?.sales_email || 'sales@inspiroworks.com'}</a>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Desk add-on purchase dialog */}
      <Dialog open={showServiceDesk} onOpenChange={setShowServiceDesk}>
        <DialogContent data-testid="service-desk-dialog">
          <DialogHeader>
            <DialogTitle>Add Inspiro Service Desk agents</DialogTitle>
            <DialogDescription>Buy agent seats for your self-service help desk. Billed per agent, {currency === 'USD' ? 'no GST (international)' : 'GST included'}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2" data-testid="sd-currency-toggle">
              <button type="button" onClick={() => changeCurrency('INR')}
                className={`flex-1 h-8 rounded-md text-[12px] font-semibold border transition-colors ${currency === 'INR' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                data-testid="sd-currency-inr">₹ INR</button>
              <button type="button" onClick={() => changeCurrency('USD')}
                className={`flex-1 h-8 rounded-md text-[12px] font-semibold border transition-colors ${currency === 'USD' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                data-testid="sd-currency-usd">$ USD</button>
            </div>
            <Tabs value={billingCycle} onValueChange={setBillingCycle}>
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="yearly">Yearly (Save 10%)</TabsTrigger>
              </TabsList>
            </Tabs>
            <div>
              <label className="block text-xs font-semibold text-zinc-600 mb-2">Number of agents</label>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => setSdAgents(Math.max(1, sdAgents - 1))} data-testid="sd-agent-decrement">−</Button>
                <div className="flex-1 text-center font-bold text-2xl" data-testid="sd-agent-count">{sdAgents}</div>
                <Button variant="outline" size="icon" onClick={() => setSdAgents(sdAgents + 1)} data-testid="sd-agent-increment">+</Button>
              </div>
            </div>
            {pricing?.service_desk && (
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-4" data-testid="sd-quote">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">{sym}{billingCycle === 'yearly' ? pricing.service_desk.yearly_per_month : pricing.service_desk.monthly} × {sdAgents} agent{sdAgents !== 1 ? 's' : ''}{billingCycle === 'yearly' ? ' / mo' : ''}</span>
                  <span className="font-bold text-orange-600 text-lg" data-testid="sd-pay-now">{sym}{(billingCycle === 'yearly' ? pricing.service_desk.yearly_per_year : pricing.service_desk.monthly) * sdAgents}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{billingCycle === 'yearly' ? 'Billed yearly (12 months)' : 'Billed monthly (30 days)'}{usdMode ? ' · no GST' : ''}</div>
              </div>
            )}
            <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => startServiceDeskCheckout(sdAgents, billingCycle)} disabled={processing}
              data-testid="sd-checkout-button">
              <CreditCard size={14} className="mr-2" /> Pay {sym}{pricing?.service_desk ? (billingCycle === 'yearly' ? pricing.service_desk.yearly_per_year : pricing.service_desk.monthly) * sdAgents : ''} with Razorpay
            </Button>
            {usdMode && (
              <p className="text-[11px] text-zinc-500 text-center" data-testid="sd-usd-contact-sales">
                Need custom USD pricing or an invoice? <a href={`mailto:${pricing?.sales_email || 'sales@inspiroworks.com'}`} className="text-orange-600 hover:underline">Contact {pricing?.sales_email || 'sales@inspiroworks.com'}</a>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reduce Seats dialog — no refund/credit for current cycle by design.
          New (lower) seat count takes effect at next renewal. */}
      <Dialog open={showReduceSeats} onOpenChange={setShowReduceSeats}>
        <DialogContent data-testid="reduce-seats-dialog">
          <DialogHeader>
            <DialogTitle>Reduce paid seats</DialogTitle>
            <DialogDescription>
              Lower your seat count to save on the next billing cycle. You keep all current seats until {status.current_period_end ? new Date(status.current_period_end).toLocaleDateString() : 'renewal'} — no refund for what you've already paid this cycle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-600 mb-2">New seat count</label>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon"
                  onClick={() => setReduceTargetSeats(Math.max(status.user_count || 1, reduceTargetSeats - 1))}
                  data-testid="reduce-seat-decrement">−</Button>
                <div className="flex-1 text-center font-bold text-2xl" data-testid="reduce-seat-count">{reduceTargetSeats}</div>
                <Button variant="outline" size="icon"
                  onClick={() => setReduceTargetSeats(Math.min(status.billable_users || 1, reduceTargetSeats + 1))}
                  data-testid="reduce-seat-increment">+</Button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Current paid seats: <b>{status.billable_users}</b> · active members: <b>{status.user_count}</b>
              </p>
              {reduceTargetSeats < (status.user_count || 0) && (
                <p className="text-[11px] text-red-600 mt-1.5" data-testid="reduce-seat-warning">
                  You can't go below {status.user_count} — that's how many people are actively in the workspace. Remove members first.
                </p>
              )}
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-[12px] space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-zinc-600">Next invoice on {status.current_period_end ? new Date(status.current_period_end).toLocaleDateString() : '—'}</span>
                <span className="font-semibold text-zinc-800">₹{reduceTargetSeats * (price)}</span>
              </div>
              <div className="text-[10px] text-zinc-500">{reduceTargetSeats} seats × ₹{price} · {status.billing_cycle || 'monthly'}</div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowReduceSeats(false)} disabled={processing}>Cancel</Button>
              <Button
                onClick={handleReduceSeats}
                disabled={processing || reduceTargetSeats < (status.user_count || 1) || reduceTargetSeats >= (status.billable_users || 1)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="reduce-seats-confirm"
              >
                Reduce to {reduceTargetSeats} seats
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Downgrade confirm */}
      <Dialog open={showDowngrade} onOpenChange={setShowDowngrade}>
        <DialogContent data-testid="downgrade-dialog">
          <DialogHeader>
            <DialogTitle>Downgrade to Free?</DialogTitle>
            <DialogDescription>
              Free plan supports up to {status.free_plan_limit} members only. You currently have {status.user_count} active members.
              {status.user_count > status.free_plan_limit && ' Please remove extra members before downgrading.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDowngrade(false)}>Cancel</Button>
            <Button onClick={handleDowngrade} disabled={processing || status.user_count > status.free_plan_limit} className="bg-red-600 hover:bg-red-700">Downgrade</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Razorpay fallback (when modal is blocked by mixed-content / iframe sandbox) */}
      <Dialog open={!!fallbackOrder} onOpenChange={() => setFallbackOrder(null)}>
        <DialogContent data-testid="razorpay-fallback-dialog">
          <DialogHeader>
            <DialogTitle>Complete your payment</DialogTitle>
            <DialogDescription>
              We'll open Razorpay's secure hosted checkout page. After payment, you'll be redirected back here automatically.
            </DialogDescription>
          </DialogHeader>
          {fallbackOrder && (
            <>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-600">Order total</span>
                  <span className="font-bold text-indigo-700 text-lg">₹{Math.round(fallbackOrder.amount / 100)}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{fallbackOrder.seat_count} seat(s) · {fallbackOrder.cycle}</div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-900 space-y-1.5">
                <p className="font-semibold">Razorpay Test Mode — use ONE of these:</p>
                <div className="border-b border-amber-200 pb-1.5">
                  <p className="font-semibold text-[11px] text-amber-800">Option A · UPI (easiest)</p>
                  <p>UPI ID: <code className="font-mono bg-amber-100 px-1 rounded">success@razorpay</code></p>
                </div>
                <div className="border-b border-amber-200 pb-1.5">
                  <p className="font-semibold text-[11px] text-amber-800">Option B · Net Banking</p>
                  <p>Select any bank · click "Success" on test page</p>
                </div>
                <div>
                  <p className="font-semibold text-[11px] text-amber-800">Option C · Card (domestic only)</p>
                  <p>Card: <code className="font-mono">4386 2894 0766 0153</code> (Visa)</p>
                  <p>or <code className="font-mono">5267 3181 8797 5449</code> (Mastercard)</p>
                  <p>Expiry: any future · CVV: any 3 digits · OTP: <code>1234</code> or "Success"</p>
                </div>
                <p className="pt-1 text-[10px] text-amber-700">⚠ Don't use 4111 1111 1111 1111 — your merchant account rejects international cards.</p>
              </div>
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={openHostedCheckout}
                disabled={processing}
                data-testid="fallback-checkout-button"
              >
                <CreditCard size={14} className="mr-2" /> {processing ? 'Preparing…' : 'Proceed to Razorpay Checkout'}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanCard({ title, price, subtitle, highlight, features, cta, onClick, ctaDisabled, dataTestId }) {
  return (
    <div className={`rounded-xl p-6 border ${highlight ? 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-white' : 'border-zinc-200 bg-white'}`} data-testid={dataTestId}>
      <h3 className="font-['Outfit'] text-xl font-bold">{title}</h3>
      <div className="mt-2 mb-4">
        <span className="text-3xl font-bold text-zinc-900">{price}</span>
        <span className="text-sm text-zinc-500 ml-1">{subtitle}</span>
      </div>
      <ul className="space-y-2 mb-5 text-sm">
        {features.map(f => <li key={f} className="flex items-center gap-2 text-zinc-700"><CheckCircle weight="fill" size={14} className="text-emerald-500" /> {f}</li>)}
      </ul>
      <Button onClick={onClick} disabled={ctaDisabled} className={`w-full ${highlight && !ctaDisabled ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`} variant={highlight && !ctaDisabled ? 'default' : 'outline'}>
        {cta}
      </Button>
    </div>
  );
}

// ─── Plans comparison block ─────────────────────────────────────────
// Always shown on the Billing page. Three side-by-side cards (Free, Business,
// Premium) with a ✓ / — feature matrix below each, then a more comprehensive
// comparison table that consolidates everything for sales/PM-style visibility.
// Service Desk product tab — dedicated pricing card + full feature showcase
// (Service Desk has many capabilities, so it gets its own spacious view
// instead of a cramped add-on strip on the Projects tab).
function ServiceDeskPlans({ status, canManage, processing, pricing, currency, openServiceDesk }) {
  const sym = pricing?.symbol || (currency === 'USD' ? '$' : '₹');
  const sdMonthly = pricing?.service_desk?.monthly;
  const sdYearlyPerMonth = pricing?.service_desk?.yearly_per_month;
  const owned = pricing?.sd_agents_owned || 0;

  const featureGroups = [
    { title: 'Self-service & portals', items: [
      'Branded Help Centre (umbrella for all portals)',
      'Multiple customer-facing portals',
      'Per-portal logo, colours & custom branding',
      'Magic-link (passwordless) customer sign-in',
      'Public knowledge base with deflection tracking',
    ]},
    { title: 'Ticketing & agent queues', items: [
      'Agent queues with saved views & filters',
      'Bulk actions & inline quick-edit',
      'Ticket routing & assignment rules',
      'Priorities, statuses & custom workflows',
      'Internal notes + public customer replies',
      'Suggested knowledge articles while replying',
      'File attachments, linked issues & watchers',
      'One-click migration from Jira Service Management (JSM import)',
    ]},
    { title: 'IT service management (ITSM)', items: [
      'CMDB — asset & configuration management',
      'Link assets to tickets & Jira project tasks',
      'Per-queue asset scoping',
      'Incident & problem management',
      'Two-way incident/problem linking with major-incident mode',
    ]},
    { title: 'Forms & request types', items: [
      'Drag-and-drop dynamic form builder',
      'Unlimited custom request types',
      'Custom fields (text, select, date, file…)',
      'Per-request-type form schemas',
    ]},
    { title: 'SLA & automation', items: [
      'SLA policies with response/resolution timers',
      'Escalation rules & breach tracking',
      'Automated agent assignment',
      'Business-hours aware SLAs',
    ]},
    { title: 'Feedback & insights', items: [
      'CSAT satisfaction surveys',
      'Queue analytics: SLA attainment, MTTR & first-response',
      'Backlog aging & created-vs-resolved trends',
      'Full ticket activity & audit history',
      'Agent & CSAT reporting',
    ]},
    { title: 'Notifications & channels', items: [
      'Rich HTML email notifications',
      'Slack & Microsoft Teams alerts',
      'Customer email updates on every reply',
    ]},
  ];

  return (
    <div className="mb-6" data-testid="service-desk-plans">
      <div className="mb-3">
        <h3 className="font-['Outfit'] text-xl font-bold text-zinc-900">Inspiro Service Desk</h3>
        <p className="text-[12px] text-zinc-500 mt-0.5">A self-service, Jira-style service-management platform — sold per agent, add it to any plan.</p>
      </div>

      {/* Hero pricing card */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 text-white p-7 mb-6 shadow-[0_10px_32px_rgba(234,88,12,0.3)]" data-testid="sd-plan-card">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center font-black text-lg">SD</div>
              <h4 className="font-['Outfit'] text-2xl font-black tracking-tight">Service Desk</h4>
              <Badge className="bg-white/20 text-white border-white/30 text-[10px]">Add-on</Badge>
            </div>
            <p className="text-[13px] text-orange-50/90 max-w-lg">Give customers a branded self-service portal to raise & track tickets, while your team manages queues, SLAs and resolutions — all in one connected workspace.</p>
            {owned > 0 && <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold bg-white/20 rounded-full px-3 py-1" data-testid="sd-owned-count">✓ You own {owned} agent{owned > 1 ? 's' : ''}</p>}
          </div>
          <div className="md:w-64 md:border-l md:border-white/20 md:pl-6">
            <div className="flex items-baseline">
              <span className="font-['Outfit'] text-[44px] font-black tabular-nums" data-testid="sd-plan-price">{sym}{sdMonthly ?? '—'}</span>
              <span className="text-[13px] text-orange-50/90 ml-1">/ agent / mo</span>
            </div>
            {sdYearlyPerMonth != null && <p className="text-[11px] text-orange-50/80 mb-4">Or {sym}{sdYearlyPerMonth}/agent/mo billed yearly · save 10%</p>}
            {canManage && (
              <Button onClick={openServiceDesk} disabled={processing}
                className="w-full bg-white text-orange-600 hover:bg-orange-50 font-bold" data-testid="sd-plan-cta">
                {owned > 0 ? 'Add more agents' : 'Add agents'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Feature showcase grid */}
      <div className="mb-2">
        <h4 className="text-[13px] font-bold text-zinc-800 mb-3">Everything included</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sd-feature-grid">
          {featureGroups.map(g => (
            <div key={g.title} className="bg-white border border-zinc-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
              <h5 className="text-[12px] font-bold text-orange-600 uppercase tracking-wide mb-3">{g.title}</h5>
              <ul className="space-y-2">
                {g.items.map(f => (
                  <li key={f} className="flex items-start gap-2 text-[12.5px] text-zinc-700">
                    <CheckCircle weight="fill" size={14} className="text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlansComparison({ status, isFree, isTrial, isActive, canManage, processing, handleStartTrial, openSubscribe, openDowngrade, pricing, currency }) {
  const freeLimit = status.free_plan_limit;
  // Currency-aware list prices from the backend pricing table (falls back to
  // the static INR constants until the table loads).
  const sym = pricing?.symbol || (currency === 'USD' ? '$' : '₹');
  const monthlyPrice = pricing?.business?.monthly ?? PLAN_LIST_PRICES.business_monthly;
  const yearlyPerUser = pricing?.business?.yearly_per_month ?? PLAN_LIST_PRICES.business_yearly;
  const premiumPrice = pricing?.premium?.monthly ?? PLAN_LIST_PRICES.premium_monthly;

  // Single source of truth for feature parity across plans. Keep ordering meaningful.
  const matrix = [
    { group: 'Workspace', rows: [
      { label: 'Team members', free: `Up to ${freeLimit}`, business: 'Unlimited', premium: 'Unlimited · min 20' },
      { label: 'Projects', free: 'Unlimited', business: 'Unlimited', premium: 'Unlimited' },
      { label: 'Storage', free: '100 MB', business: '10 GB', premium: '200 GB' },
      { label: 'File upload size (per file)', free: '5 MB', business: '10 MB', premium: '100 MB' },
      { label: 'Dedicated storage configuration', free: false, business: false, premium: true },
      { label: 'Region-based data storage (EU / US / APAC)', free: false, business: false, premium: true },
      { label: 'Project templates (Scrum, Kanban, Bug Tracker, Blank)', free: true, business: true, premium: true },
      { label: 'Docs / Pages per project', free: '—', business: 'Up to 15', premium: 'Unlimited' },
      { label: 'Whiteboards per project', free: '—', business: 'Up to 5', premium: 'Unlimited' },
      { label: 'Sprints & backlog planning', free: true, business: true, premium: true },
      { label: 'Kanban, List & Subtasks', free: true, business: true, premium: true },
      { label: 'Custom fields, statuses & customizable card layout', free: true, business: true, premium: true },
      { label: 'Custom domain (workspace.yourdomain.com)', free: false, business: false, premium: true },
      { label: 'Custom branding & white-label', free: false, business: false, premium: true },
    ]},
    { group: 'Collaboration', rows: [
      { label: 'Rich-text editor (image paste, code blocks, links)', free: true, business: true, premium: true },
      { label: 'Comments, @mentions, reactions', free: true, business: true, premium: true },
      { label: 'File attachments (PDFs, images, docs, video)', free: true, business: true, premium: true },
      { label: 'Linked issues & dependency-aware Done guard', free: true, business: true, premium: true },
      { label: 'Task watchers & subscriptions', free: true, business: true, premium: true },
      { label: 'Email notifications (rich HTML)', free: true, business: true, premium: true },
      { label: 'Custom email notifications (branded templates)', free: false, business: false, premium: true },
      { label: 'Activity / audit history per task', free: true, business: true, premium: true },
      { label: 'Bulk operations (move, assign, delete)', free: false, business: true, premium: true },
    ]},
    { group: 'Insights & analytics', rows: [
      { label: 'Home dashboard + Risk radar', free: true, business: true, premium: true },
      { label: 'Project Health scorecard', free: false, business: true, premium: true },
      { label: 'Sprint velocity & throughput charts', free: false, business: true, premium: true },
      { label: 'Cycle time + leaderboard', free: false, business: true, premium: true },
      { label: 'Sprint burndown chart', free: false, business: true, premium: true },
      { label: 'Storage usage breakdown by project', free: false, business: true, premium: true },
      { label: 'Advanced analytics & insights', free: false, business: false, premium: true },
    ]},
    { group: 'Integrations', rows: [
      { label: 'Slack — OAuth, slash commands, replies', free: false, business: true, premium: true },
      { label: 'Microsoft Teams — OAuth & notifications', free: false, business: true, premium: true },
      { label: 'Jira Cloud import & migration (projects, issues, comments, users)', free: false, business: true, premium: true },
      { label: 'CSV import / export', free: false, business: true, premium: true },
      { label: 'Public REST API access', free: false, business: false, premium: true },
    ]},
    { group: 'Security & admin', rows: [
      { label: 'Tenant-isolated multi-org workspace', free: true, business: true, premium: true },
      { label: 'JWT auth, password reset, brute-force protection', free: true, business: true, premium: true },
      { label: 'Project-level role permissions (owner/admin/PM/member/viewer)', free: true, business: true, premium: true },
      { label: 'Email verification & invite links', free: true, business: true, premium: true },
      { label: 'Single sign-on (Google & Microsoft)', free: false, business: true, premium: true },
      { label: 'Enterprise SSO / SAML (Okta, Azure AD, Custom IdP)', free: false, business: false, premium: true },
    ]},
    { group: 'Support', rows: [
      { label: 'In-product support tickets', free: false, business: true, premium: true },
      { label: 'Priority email support', free: false, business: true, premium: true },
      { label: 'Priority support with SLA (99.9% uptime)', free: false, business: false, premium: true },
    ]},
  ];

  const freeCta = isFree
    ? { label: '✓ Your current plan', disabled: true, onClick: () => {} }
    : isActive
      ? { label: 'Downgrade to Free', disabled: false, onClick: openDowngrade, variant: 'outline' }
      : { label: 'Trial ends → goes to Free', disabled: true, onClick: () => {} };

  const businessCta = isActive
    ? { label: '✓ Your current plan', disabled: true, onClick: () => {}, variant: 'outline' }
    : isTrial
      ? { label: 'Subscribe now', disabled: false, onClick: openSubscribe }
      : status.trial_used
        ? { label: 'Subscribe to Business', disabled: false, onClick: openSubscribe }
        : { label: 'Start 14-day free trial', disabled: processing, onClick: handleStartTrial };

  return (
    <div className="mb-6" data-testid="plans-comparison">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-['Outfit'] text-xl font-bold text-zinc-900">Inspiro Projects plans</h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">Two simple tiers — pick what fits your team.</p>
        </div>
      </div>

      {/* Hero plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* FREE */}
        <div className={`relative rounded-xl border p-6 transition-shadow ${isFree ? 'border-emerald-300 bg-gradient-to-br from-emerald-50/60 to-white shadow-sm' : 'border-zinc-200 bg-white'}`} data-testid="compare-plan-free">
          {isFree && <span className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider">Current</span>}
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-['Outfit'] text-2xl font-black tracking-tight">Free</h4>
            <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200 text-[10px]">Forever</Badge>
          </div>
          <p className="text-[12px] text-zinc-500 mb-4">Best for small teams getting started.</p>
          <div className="flex items-baseline mb-1">
            <span className="font-['Outfit'] text-[40px] font-black tabular-nums">{sym}0</span>
            <span className="text-[12px] text-zinc-500 ml-1">/ forever</span>
          </div>
          <p className="text-[11px] text-zinc-400 mb-5">Up to {freeLimit} team members · unlimited projects</p>
          <ul className="space-y-1.5 mb-5">
            {[
              `Up to ${freeLimit} members`,
              'Unlimited projects, sprints & backlogs',
              '5 MB max per file upload · 100 MB total storage',
              'Kanban, List, Subtasks, Custom fields',
              'Comments, mentions, attachments, links, watchers',
              'Email notifications + Home dashboard',
            ].map(f => (
              <li key={f} className="flex items-start gap-2 text-[12px] text-zinc-700">
                <CheckCircle weight="fill" size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {canManage && (
            <Button onClick={freeCta.onClick} disabled={freeCta.disabled} variant={freeCta.variant || 'outline'}
              className="w-full" data-testid="compare-cta-free">
              {freeCta.label}
            </Button>
          )}
        </div>

        {/* BUSINESS */}
        <div className={`relative rounded-xl border-2 p-6 transition-shadow ${isActive || isTrial ? 'border-indigo-400 bg-gradient-to-br from-indigo-50 to-white shadow-md' : 'border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white hover:shadow-md'}`} data-testid="compare-plan-business">
          {(isActive || isTrial) && <span className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-black uppercase tracking-wider">{isTrial ? 'Trial' : 'Current'}</span>}
          {isFree && <span className="absolute -top-2.5 right-6 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1"><Sparkle size={9} weight="fill" /> Recommended</span>}
          <div className="flex items-center gap-2 mb-1">
            <Crown size={18} weight="fill" className="text-indigo-600" />
            <h4 className="font-['Outfit'] text-2xl font-black tracking-tight">Business</h4>
          </div>
          <p className="text-[12px] text-zinc-500 mb-4">For growing teams that need analytics & integrations.</p>
          <div className="flex items-baseline mb-1">
            <span className="font-['Outfit'] text-[40px] font-black tabular-nums text-indigo-700" data-testid="compare-business-price">{sym}{monthlyPrice}</span>
            <span className="text-[12px] text-zinc-500 ml-1">/ user / month</span>
          </div>
          <p className="text-[11px] text-zinc-400 mb-5">Or {sym}{yearlyPerUser}/user/month billed yearly · save 10%</p>
          <ul className="space-y-1.5 mb-5">
            {[
              'Everything in Free, plus:',
              'Unlimited team members',
              '10 MB max per file upload · 10 GB total storage',
              'Project Health · Velocity · Throughput · Cycle time',
              'Sprint burndown chart + Leaderboard',
              'Slack & Microsoft Teams integrations',
              'Single sign-on (Google & Microsoft)',
              'CSV import / export · Priority email support',
            ].map((f, i) => (
              <li key={f} className={`flex items-start gap-2 text-[12px] ${i === 0 ? 'text-indigo-700 font-bold' : 'text-zinc-700'}`}>
                {i === 0 ? <Sparkle size={13} weight="fill" className="text-indigo-500 mt-0.5 flex-shrink-0" /> : <CheckCircle weight="fill" size={13} className="text-indigo-500 mt-0.5 flex-shrink-0" />}
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {canManage && (
            <Button onClick={businessCta.onClick} disabled={businessCta.disabled}
              variant={businessCta.variant}
              className={`w-full ${businessCta.variant === 'outline' ? '' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              data-testid="compare-cta-business">
              {businessCta.label === 'Start 14-day free trial' && <Sparkle size={13} className="mr-1.5" />}
              {businessCta.label}
            </Button>
          )}
        </div>

        {/* PREMIUM — sales-led, dark luxe card */}
        <div className="relative rounded-xl p-6 bg-gradient-to-br from-zinc-900 via-slate-900 to-indigo-950 text-white shadow-[0_10px_32px_rgba(30,27,75,0.35)] ring-1 ring-white/10" data-testid="compare-plan-premium">
          <span className="absolute -top-2.5 right-6 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1">
            <Crown size={9} weight="fill" /> Enterprise
          </span>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
              <Crown size={14} weight="fill" className="text-white" />
            </div>
            <h4 className="font-['Outfit'] text-2xl font-black tracking-tight">Premium</h4>
          </div>
          <p className="text-[12px] text-indigo-200 mb-4">For enterprises that need dedicated infra, branding & SLA.</p>
          <div className="flex items-baseline mb-1">
            <span className="font-['Outfit'] text-[40px] font-black tabular-nums text-white" data-testid="compare-premium-price">{sym}{premiumPrice}</span>
            <span className="text-[12px] text-indigo-200 ml-1">/ user / month</span>
          </div>
          <p className="text-[11px] text-indigo-300 mb-5">Minimum 20 users · annual contract</p>
          <ul className="space-y-1.5 mb-5">
            {[
              'Everything in Business, plus:',
              '200 GB storage · dedicated storage configs',
              'Region-based data storage (EU / US / APAC)',
              'Custom domain · custom branding & white-label',
              'Enterprise SSO / SAML (Okta, Custom IdP) · REST API access',
              'Priority support with 99.9% uptime SLA',
            ].map((f, i) => (
              <li key={f} className={`flex items-start gap-2 text-[12px] ${i === 0 ? 'text-amber-300 font-bold' : 'text-zinc-100'}`}>
                {i === 0 ? <Sparkle size={13} weight="fill" className="text-amber-400 mt-0.5 flex-shrink-0" /> : <CheckCircle weight="fill" size={13} className="text-emerald-400 mt-0.5 flex-shrink-0" />}
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <a
            href={`mailto:support@inspiroworks.com?subject=${encodeURIComponent('Inspiro Projects — Premium plan inquiry')}&body=${encodeURIComponent('Hi Inspiro Projects team,\n\nWe\'d like to upgrade our workspace to the Premium plan.\n\nTeam size: \nWorkspace name: \nPreferred start date: \n\nThanks!')}`}
            className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white text-[13px] font-bold tracking-tight shadow-md px-4"
            data-testid="compare-cta-premium"
          >
            <FileText size={13} weight="bold" />
            Contact Support to Upgrade
          </a>
          <p className="mt-2 text-[10.5px] text-indigo-300 text-center">
            or email <a href="mailto:support@inspiroworks.com" className="underline underline-offset-2 text-amber-300">support@inspiroworks.com</a>
          </p>
        </div>
      </div>

      {/* Detailed feature matrix */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="plans-feature-matrix">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h4 className="text-[13px] font-bold text-zinc-800">Full feature comparison</h4>
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">All limits at a glance</span>
        </div>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left border-b border-zinc-200 bg-zinc-50">
              <th className="px-5 py-3 font-bold text-zinc-600 w-[40%]">Feature</th>
              <th className="px-5 py-3 font-bold text-zinc-600 text-center w-[20%]">Free</th>
              <th className="px-5 py-3 font-bold text-indigo-700 text-center w-[20%]">Business</th>
              <th className="px-5 py-3 font-bold text-amber-700 text-center w-[20%]">
                <span className="inline-flex items-center gap-1">
                  <Crown size={12} weight="fill" className="text-amber-600" />
                  Premium
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {matrix.map(group => (
              <React.Fragment key={group.group}>
                {/* Distinct section header — gradient indigo strip so each
                    bucket is visually separated from the rows below. */}
                <tr className="bg-gradient-to-r from-indigo-50 via-violet-50 to-indigo-50 border-y border-indigo-100">
                  <td colSpan={4} className="px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-black text-indigo-700">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-1 h-3.5 rounded-sm bg-gradient-to-b from-indigo-500 to-violet-500" />
                      {group.group}
                    </span>
                  </td>
                </tr>
                {group.rows.map(row => (
                  <tr key={row.label} className="border-b border-zinc-50 hover:bg-zinc-50/40">
                    <td className="px-5 py-2.5 text-zinc-700">{row.label}</td>
                    <td className="px-5 py-2.5 text-center">
                      {typeof row.free === 'boolean' ? (
                        row.free
                          ? <CheckCircle weight="fill" size={15} className="text-emerald-500 inline-block" />
                          : <MinusCircle weight="fill" size={15} className="text-zinc-300 inline-block" />
                      ) : (
                        <span className="font-semibold text-zinc-700">{row.free}</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-center">
                      {typeof row.business === 'boolean' ? (
                        row.business
                          ? <CheckCircle weight="fill" size={15} className="text-indigo-600 inline-block" />
                          : <MinusCircle weight="fill" size={15} className="text-zinc-300 inline-block" />
                      ) : (
                        <span className="font-semibold text-indigo-700">{row.business}</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-center">
                      {typeof row.premium === 'boolean' ? (
                        row.premium
                          ? <CheckCircle weight="fill" size={15} className="text-amber-500 inline-block" />
                          : <MinusCircle weight="fill" size={15} className="text-zinc-300 inline-block" />
                      ) : (
                        <span className="font-semibold text-amber-700">{row.premium}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <div className="px-5 py-3 bg-zinc-50/50 border-t border-zinc-100 flex items-center gap-2 text-[11px] text-zinc-500">
          <Lock size={12} className="text-zinc-400" />
          <span>Need SSO, dedicated support, or a custom contract? <a href="mailto:support@inspiroworks.com" className="text-indigo-600 hover:underline font-semibold">Talk to sales</a>.</span>
        </div>
      </div>
    </div>
  );
}
